// Offering a child to a contracting agency: what an offer says, how long it
// lasts, and what it reads as on every screen that shows one — the office's
// Agency scheduling board, the child's Schedule tab, the agency's own board,
// the Daily Digest and the Chat notices (edge functions, through _shared/lib).
//
// The lifecycle (decided 2026-10-28; migration 20261028202329):
//   open → accepted | declined | expired | withdrawn | filled
//   accepted → filled (their nurse reaches the calendar) | withdrawn
// An open offer lasts DEFAULT_OFFER_DAYS unless the office chose otherwise,
// and can be extended. public.close_agency_offers() expires and fills offers
// every hour; the agency answers through respond-agency-offer.
//
// Runtime-neutral: no imports, nothing browser- or Deno-specific.

export const DEFAULT_OFFER_DAYS = 7;
export const OFFER_DAY_CHOICES = [2, 7, 14];

/** Offers that still put the child on the agency's board. */
export const LIVE_OFFER_STATUSES = ['open', 'accepted'];

export const ROLE_NEEDED = [
  { value: 'rn', label: 'RN' },
  { value: 'lpn', label: 'LPN' },
  { value: 'pca', label: 'PCA' },
  { value: 'any', label: 'Any nurse' },
];
export const roleLabel = (v) => ROLE_NEEDED.find((r) => r.value === v)?.label || null;

export const OFFER_STATUS_LABEL = {
  open: 'Waiting for their answer',
  accepted: 'They can cover',
  declined: "They can't cover",
  expired: 'No answer — expired',
  withdrawn: 'Withdrawn',
  filled: 'Filled',
};

const DAY = 86_400_000;
const iso = (d) => (d instanceof Date ? d : new Date(d)).toISOString();

/** When an offer made now for `days` lapses. */
export const offerExpiry = (days = DEFAULT_OFFER_DAYS, now = new Date()) => iso(new Date(new Date(now).getTime() + days * DAY));

/** An open offer whose window has passed (the hourly job will close it; screens should not wait). */
export const isExpired = (offer, now = new Date()) =>
  offer?.status === 'open' && !!offer.expires_at && Date.parse(offer.expires_at) <= new Date(now).getTime();

/** Does this offer still put the child on the agency's board? */
export const isLiveOffer = (offer, now = new Date()) =>
  !!offer && LIVE_OFFER_STATUSES.includes(offer.status) && !isExpired(offer, now);

/** Whole days left on an open offer, never negative; null when it has no end or is not open. */
export const daysLeft = (offer, now = new Date()) => {
  if (offer?.status !== 'open' || !offer.expires_at) return null;
  return Math.max(0, Math.ceil((Date.parse(offer.expires_at) - new Date(now).getTime()) / DAY));
};

/** "RN · 24h a week · from 10 Nov" — what is being asked for, for any screen. */
export const offerSummary = (offer) => {
  const parts = [
    roleLabel(offer?.role_needed),
    offer?.hours_needed ? `${Number(offer.hours_needed)}h a week` : null,
    offer?.start_date ? `from ${offer.start_date}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'For discussion';
};

/** One line on where an offer stands, for the office. */
export const offerStatusText = (offer, now = new Date()) => {
  if (!offer) return '';
  if (isExpired(offer, now)) return OFFER_STATUS_LABEL.expired;
  if (offer.status === 'open') {
    const d = daysLeft(offer, now);
    return d === null ? OFFER_STATUS_LABEL.open : `${OFFER_STATUS_LABEL.open} · ${d === 0 ? 'expires today' : `${d} day${d === 1 ? '' : 's'} left`}`;
  }
  const who = offer.responded_by_name ? ` (${offer.responded_by_name})` : '';
  const why = offer.response_note || offer.closed_reason;
  return `${OFFER_STATUS_LABEL[offer.status] || offer.status}${who}${why ? ` — ${why}` : ''}`;
};

/**
 * The row for a new offer from the office's form. Hours, role and start date
 * are optional: an offer with none of them is a child offered for discussion.
 */
export const newOfferRow = ({ clientId, agencyId, form = {}, profile = null, now = new Date() }) => {
  const hours = Number(form.hours_needed);
  const days = OFFER_DAY_CHOICES.includes(Number(form.days)) ? Number(form.days) : DEFAULT_OFFER_DAYS;
  const role = ROLE_NEEDED.some((r) => r.value === form.role_needed) ? form.role_needed : null;
  return {
    client_id: clientId,
    contracting_agency_id: agencyId,
    status: 'open',
    role_needed: role,
    hours_needed: Number.isFinite(hours) && hours > 0 ? Math.round(hours * 10) / 10 : null,
    start_date: /^\d{4}-\d{2}-\d{2}$/.test(String(form.start_date || '')) ? form.start_date : null,
    note: String(form.note || '').trim().slice(0, 500) || null,
    service_type: role === 'pca' ? 'pss' : role ? 'skilled' : 'discussion',
    expires_at: offerExpiry(days, now),
    created_by: profile?.id || null,
    created_by_name: profile?.full_name || null,
  };
};

/** The update that gives an open offer `days` more, from now or from its current end, whichever is later. */
export const extendOfferUpdate = (offer, days = DEFAULT_OFFER_DAYS, now = new Date()) => {
  const from = Math.max(new Date(now).getTime(), offer?.expires_at ? Date.parse(offer.expires_at) : 0);
  return { expires_at: iso(new Date(from + days * DAY)), updated_at: iso(now) };
};

/** The agency's answer, made safe to store. Returns null when it cannot be accepted. */
export const offerAnswer = ({ answer, name, note }, now = new Date()) => {
  const status = answer === 'accept' ? 'accepted' : answer === 'decline' ? 'declined' : null;
  const who = String(name || '').replace(/\s+/g, ' ').trim().slice(0, 120);
  if (!status || !who) return null;
  return {
    status,
    responded_at: iso(now),
    responded_by_name: who,
    response_note: String(note || '').trim().slice(0, 500) || null,
    updated_at: iso(now),
  };
};
